/**
 * Enhanced Router System for Store Autonomy
 * Supports dynamic routing based on store-specific pages and layouts
 */

import React, { Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme.js';
import { loadStoreModule, getStoreCapabilities } from '../utils/storeModuleLoader.js';

// Core system components (always available)
import DefaultLayout from '../layouts/DefaultLayout.jsx';
import Homepage from '../pages/Homepage.jsx';
import AboutPage from '../pages/AboutPage.jsx';
import ProductCategoryPage from '../pages/ProductCategoryPage.jsx';
import ProductDetailPage from '../pages/ProductDetailPage.jsx';
import FranchisePage from '../pages/FranchisePage.jsx';
import ServicesPage from '../pages/ServicesPage.jsx';
import StoreLocatorPage from '../pages/StoreLocatorPage.jsx';
import ThemeSwitcher from '../pages/ThemeSwitcher.jsx';
import NotFound from '../pages/NotFound.jsx';

/**
 * Loading fallback component
 */
const LoadingFallback = ({ message = "Loading..." }) => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-text-secondary">{message}</p>
    </div>
  </div>
);

/**
 * Dynamic Page Loader Component
 * Loads store-specific pages with fallback to core pages
 */
const DynamicPage = ({ pageComponent: CorePageComponent, pageName, storeId }) => {
  const [StorePageComponent, setStorePageComponent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadStorePage = async () => {
      if (!storeId) {
        setStorePageComponent(() => CorePageComponent);
        setIsLoading(false);
        return;
      }

      try {
        console.log(`🔄 Loading store page: ${storeId}/${pageName}`);
        
        // Clean page name mapping for store-specific pages
        const pageNameMapping = {
          'StoreProducts': 'AnticProductsPage',
          'StoreProductCategory': 'AnticProductsPage', 
          'StoreProductDetail': 'AnticProductDetailPage',
          'StoreLocator': 'AnticStoreLocatorPage',
          'StoreFranchise': 'AnticFranchisePage',
          'StoreAbout': 'AnticHeritagePage',
          'StoreServices': 'AnticCustomerServicePage'
        };
        
        const actualPageName = pageNameMapping[pageName] || pageName;
        
        // Try to load store-specific page
        const storePages = await loadStoreModule(storeId, 'pages');
        
        if (storePages && storePages[actualPageName]) {
          console.log(`✅ Loaded store-specific page: ${actualPageName} (mapped from ${pageName})`);
          setStorePageComponent(() => storePages[actualPageName]);
        } else {
          console.log(`⚠️ Store page ${actualPageName} not found, using core page`);
          setStorePageComponent(() => CorePageComponent);
        }
      } catch (error) {
        console.error(`❌ Error loading store page ${pageName}:`, error);
        setError(error);
        setStorePageComponent(() => CorePageComponent);
      } finally {
        setIsLoading(false);
      }
    };

    loadStorePage();
  }, [storeId, pageName, CorePageComponent]);

  if (isLoading) {
    return <LoadingFallback message={`Loading ${pageName}...`} />;
  }

  if (error) {
    console.warn(`Using core page ${pageName} due to error:`, error);
  }

  if (!StorePageComponent) {
    return <CorePageComponent />;
  }

  return <StorePageComponent />;
};

/**
 * Dynamic Layout Loader Component
 * Loads store-specific layouts with fallback to default layout
 */
const DynamicLayout = ({ children, storeId }) => {
  const [StoreLayoutComponent, setStoreLayoutComponent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadStoreLayout = async () => {
      if (!storeId) {
        setStoreLayoutComponent(() => DefaultLayout);
        setIsLoading(false);
        return;
      }

      try {
        console.log(`🔄 Loading store layout for: ${storeId}`);
        
        // Try to load store-specific layout
        const storeLayouts = await loadStoreModule(storeId, 'layouts');
        
        if (storeLayouts && storeLayouts.StoreLayout) {
          console.log(`✅ Loaded store-specific layout for ${storeId}`);
          setStoreLayoutComponent(() => storeLayouts.StoreLayout);
        } else if (storeLayouts && storeLayouts[`${storeId}Layout`]) {
          console.log(`✅ Loaded ${storeId}Layout`);
          setStoreLayoutComponent(() => storeLayouts[`${storeId}Layout`]);
        } else {
          console.log(`⚠️ Store layout not found, using default layout`);
          setStoreLayoutComponent(() => DefaultLayout);
        }
      } catch (error) {
        console.error(`❌ Error loading store layout:`, error);
        setStoreLayoutComponent(() => DefaultLayout);
      } finally {
        setIsLoading(false);
      }
    };

    loadStoreLayout();
  }, [storeId]);

  if (isLoading) {
    return <LoadingFallback message="Loading layout..." />;
  }

  const LayoutComponent = StoreLayoutComponent || DefaultLayout;
  
  return <LayoutComponent>{children}</LayoutComponent>;
};

/**
 * Route definitions with store awareness
 */
const getRouteDefinitions = (storeId) => [
  {
    path: '/',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={Homepage} 
          pageName="StoreHomepage" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/about',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={AboutPage} 
          pageName="StoreAbout" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  // About subpages
  {
    path: '/about/history',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={AboutPage} 
          pageName="AnticHeritagePage" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/about/craftsmanship',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={AboutPage} 
          pageName="AnticCraftsmanshipPage" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/about/commitments',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={AboutPage} 
          pageName="AnticCommitmentsPage" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/products',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={ProductCategoryPage} 
          pageName="StoreProducts" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  // Product category specific routes
  {
    path: '/products/breads',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={ProductCategoryPage} 
          pageName="AnticBreadsPage" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/products/pastries',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={ProductCategoryPage} 
          pageName="AnticPastriesPage" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/products/viennoiseries',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={ProductCategoryPage} 
          pageName="AnticViennoiseriesPage" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/products/specialties',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={ProductCategoryPage} 
          pageName="AnticSpecialtiesPage" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/products/:category',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={ProductCategoryPage} 
          pageName="StoreProductCategory" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/product/:id',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={ProductDetailPage} 
          pageName="StoreProductDetail" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/locator',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={StoreLocatorPage} 
          pageName="StoreLocator" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/stores',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={StoreLocatorPage} 
          pageName="StoreLocator" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/franchise',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={FranchisePage} 
          pageName="StoreFranchise" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  // Franchise subpages
  {
    path: '/franchise/opportunities',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={FranchisePage} 
          pageName="AnticFranchiseOpportunitiesPage" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/franchise/support',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={FranchisePage} 
          pageName="AnticFranchiseSupportPage" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/franchise/contact',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={FranchisePage} 
          pageName="AnticFranchiseContactPage" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  },
  {
    path: '/services',
    element: (
      <DynamicLayout storeId={storeId}>
        <DynamicPage 
          pageComponent={ServicesPage} 
          pageName="StoreServices" 
          storeId={storeId} 
        />
      </DynamicLayout>
    )
  }
];

/**
 * Main Enhanced App Router
 */
export function EnhancedAppRouter() {
  const { currentTheme, isLoading: themeLoading } = useTheme();
  const [storeCapabilities, setStoreCapabilities] = useState(null);
  const [routesLoaded, setRoutesLoaded] = useState(false);

  // Get current store ID - Use the theme's actual ID, don't force a default
  const storeId = currentTheme?.id;

  // Load store capabilities when theme changes
  useEffect(() => {
    const loadCapabilities = async () => {
      if (!storeId) return;

      try {
        console.log(`📊 Loading capabilities for store: ${storeId}`);
        const capabilities = await getStoreCapabilities(storeId);
        setStoreCapabilities(capabilities);
        setRoutesLoaded(true);
        console.log(`✅ Store capabilities loaded:`, capabilities);
      } catch (error) {
        console.error(`❌ Error loading store capabilities:`, error);
        setStoreCapabilities({
          storeId,
          capabilities: {},
          autonomyLevel: 0,
          isFullyAutonomous: false
        });
        setRoutesLoaded(true);
      }
    };

    if (storeId && !themeLoading) {
      loadCapabilities();
    }
  }, [storeId, themeLoading]);

  // Show loading while theme is loading or we don't have a storeId yet
  if (themeLoading || !storeId || !routesLoaded) {
    return <LoadingFallback message="Loading application..." />;
  }

  // Get route definitions for current store
  const routes = getRouteDefinitions(storeId);

  console.log(`🚀 Rendering routes for store: ${storeId}`);
  console.log(`📊 Store autonomy level: ${storeCapabilities?.autonomyLevel || 0}`);

  return (
    <Router>
      <Suspense fallback={<LoadingFallback message="Loading page..." />}>
        <Routes>
          {/* Store-specific routes */}
          {routes.map((route, index) => (
            <Route key={`${storeId}-${route.path}-${index}`} {...route} />
          ))}
          
          {/* System routes (always core) */}
          <Route 
            path="/themes" 
            element={
              <DefaultLayout>
                <ThemeSwitcher />
              </DefaultLayout>
            } 
          />
          
          {/* Redirect and fallback routes */}
          <Route path="/index.html" element={<Navigate to="/" replace />} />
          <Route 
            path="*" 
            element={
              <DynamicLayout storeId={storeId}>
                <NotFound />
              </DynamicLayout>
            } 
          />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default EnhancedAppRouter;
